import type { ApprovalRule } from '../approvals.js';
import { DomainError } from '../errors.js';
import { type ApprovalChain, parseApprovalChain } from './approval-chain.js';

/**
 * ISM formu ofis onayına gitmez (sahip kararı 2026-10-17, ortağın geri bildirimi; ADR 0073):
 * kaptan onaylar ve kayıt kapanır, ofis yalnız görür. Şirkete onaya giden kayıtlar deficiency,
 * uygunsuzluk ve satın almadır; onların ofis adımı değişmez. Bu modül saftır.
 *
 * - Yeni zincirde ve onay matrisinin `ism_form` kuralında ofis adımı kurulamaz
 *   (`FORM_OFFICE_STEP_NOT_ALLOWED`).
 * - Eski ayarlar deploy'da yeni sürümle ofissiz yazılır (`formRuleWithoutOfficeSteps`,
 *   `withoutOfficeSteps`); eski matris sürümleri ve gönderim kopyaları değişmez, bu yüzden ofis
 *   adımında bekleyen kayıt kendi kuralıyla ofiste karara bağlanır.
 */

/** Hata kodu: ISM formunda ofis onay adımı artık kurulamaz (ADR 0073). */
export const FORM_OFFICE_STEP_NOT_ALLOWED = 'FORM_OFFICE_STEP_NOT_ALLOWED';

const officeStepError = (what: string) => new DomainError(FORM_OFFICE_STEP_NOT_ALLOWED, what);

/**
 * Ofiste kaydedilen yeni zincir (ADR 0071 §2 + ADR 0073): `parseApprovalChain`'in bütün kuralları
 * ve ofis adımı yok. Okumada (`parseApprovalChain`, anlık kopya) ofis adımı hâlâ okunur: eski
 * gönderimler kendi zinciriyle yürür.
 */
export function parseNewApprovalChain(value: unknown, roleIds?: readonly string[]): ApprovalChain {
  const chain = parseApprovalChain(value, roleIds);
  if (chain.steps.some((step) => step.kind === 'office')) throw officeStepError('chain');
  return chain;
}

/** Kayıtlı zincirin ofissiz hali: gemi adımları aynen kalır (deploy ve yeni gönderim). */
export function withoutOfficeSteps(chain: ApprovalChain): ApprovalChain {
  if (!chain.steps.some((step) => step.kind === 'office')) return chain;
  return { steps: chain.steps.filter((step) => step.kind !== 'office') };
}

/** Onay matrisinin ISM formu kuralında ofis adımı var mı. */
export function formRuleHasOfficeStep(rule: ApprovalRule): boolean {
  return (
    rule.recordType === 'ism_form' &&
    rule.steps.some((_, index) => rule.officeSteps?.[index] === true)
  );
}

/**
 * Doğrulama (`validateApprovalMatrix`): ISM formu kuralında ofis adımı yok. Ofis işareti kapalı
 * (`false`) dizi eski satırlardan gelebilir; sorun değildir.
 */
export function assertFormRuleWithoutOfficeStep(rule: ApprovalRule): void {
  if (formRuleHasOfficeStep(rule)) throw officeStepError(rule.recordType);
}

/**
 * Eski ISM formu kuralının ofissiz hali (deploy; ADR 0073 "Geçiş"):
 * - Ofis adımları düşer, gemi adımları sırasıyla kalır.
 * - Hiç adım kalmazsa: giren yalnız kaptansa kural adımsızdır, kaptanın gönderimi kaydı kapatır
 *   ("kaptan onaylayıp kapatıyor"); süre anlamsızdır, düşer. Öteki girenlerde kaptan onaylar
 *   (daha sıkı okuma: kayıt onaysız kapanmaz). Şirkette "Master" rolü yoksa kural adımsızdır.
 * Ofis adımı olmayan kural aynı nesneyle döner.
 */
export function formRuleWithoutOfficeSteps(
  rule: ApprovalRule,
  masterRoleId: string | null,
): ApprovalRule {
  if (!formRuleHasOfficeStep(rule)) return rule;
  const { officeSteps: _office, deadlineHours, ...rest } = rule;
  const ship = rule.steps.filter((_, index) => rule.officeSteps?.[index] !== true);
  if (ship.length > 0) {
    return { ...rest, steps: ship, ...(deadlineHours !== undefined ? { deadlineHours } : {}) };
  }
  const masterOnly =
    rule.enteredBy !== 'any' &&
    masterRoleId !== null &&
    rule.enteredBy.every((role) => role === masterRoleId);
  if (masterOnly || masterRoleId === null) return { ...rest, steps: [] };
  return {
    ...rest,
    steps: [masterRoleId],
    ...(deadlineHours !== undefined ? { deadlineHours } : {}),
  };
}
